import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Avatar from "./Avatar";
import { ReviewComment, getCommentAuthorDisplay } from "../types/reviewComment";
import Gap from "./Gap";
import { reviewApi } from "../api/review";
import { toastUtil } from "../utils/toast";
import { useAuthStore } from "../stores/authStore";
import { dialogUtil } from "../utils/dialog";
import HeartIcon from "./HeartIcon";
import { splitMentions } from "../utils/mention";

const COMMENT_LONG_PRESS_MS = 1000;

interface ReviewCommentBoxProps {
  comment: ReviewComment;
  depth: number;
  reviewId: string;
  onReplyClick: (commentId: string) => void;
  onEditClick?: (commentId: string) => void;
  onCommentDeleted?: () => void;
  /**
   * 이 기록을 쓴 사람이 보고 있나.
   *
   * 글쓴이는 **남이 쓴 댓글도** 지울 수 있다 — 내 기록에 달린 말을 치우는 일이다.
   * 수정은 아니다. 남의 말을 고치는 것은 치우는 것과 다른 일이라 작성자만 한다.
   */
  isReviewOwner?: boolean;
  /**
   * 이 댓글에 딸린 답글 수 (자손 전부).
   *
   * DB 에서 reply_id 가 cascade 라 부모를 지우면 답글이 **같이** 사라진다.
   * 확인창에 몇 개가 함께 없어지는지 적으려고 받는다.
   */
  replyCount?: number;
  /** 좋아요 토글 결과를 부모(comments state)에 반영 — stale 재시드로 "두 번 눌러야" 하는 것 방지 */
  onLikeChange?: (
    commentId: string,
    isLiked: boolean,
    likeCount: number,
  ) => void;
}

const MAX_DEPTH = 1;

const ReviewCommentBox: React.FC<ReviewCommentBoxProps> = ({
  comment,
  depth,
  reviewId,
  onReplyClick,
  onEditClick,
  onCommentDeleted,
  isReviewOwner = false,
  replyCount = 0,
  onLikeChange,
}) => {
  const navigate = useNavigate();
  const { userId: currentUserId } = useAuthStore();
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMine = !!(
    currentUserId &&
    comment.userId &&
    comment.userId === currentUserId
  );
  /**
   * 지울 수 있는 사람은 둘 — 쓴 사람과 글쓴이. 서버(deleteComment)와 같은 규칙이다.
   * 글쓴이의 삭제는 남의 말을 치우는 일이라 확인 문구를 다르게 쓴다.
   */
  const canDelete = isMine || isReviewOwner;
  const authorDisplay = getCommentAuthorDisplay(comment.user);
  const [isLiked, setIsLiked] = useState(comment.isLiked ?? false);
  const [likeCount, setLikeCount] = useState(comment.likes);
  const [isToggling, setIsToggling] = useState(false);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearLongPressTimer();
    };
  }, [clearLongPressTimer]);

  const requestDelete = useCallback(async () => {
    /*
     * 답글은 **함께** 사라진다 (review_comments.reply_id 가 onDelete cascade).
     * 내 댓글 하나 지우는 줄 알았는데 남이 단 답글까지 없어지면 되돌릴 수 없다
     * (SoftDeletes 가 없어 복구도 안 된다). 몇 개가 같이 없어지는지 먼저 말한다.
     */
    const head = isMine ? "댓글을 삭제하시겠어요?" : "이 댓글을 삭제하시겠어요?";
    const message =
      replyCount > 0 ? `${head}\n답글 ${replyCount}개도 함께 사라져요` : head;

    const confirmed = await dialogUtil.confirm({
      message,
      confirmText: "삭제",
      cancelText: "취소",
    });

    if (!confirmed) return;

    try {
      await reviewApi.deleteComment(parseInt(reviewId), parseInt(comment.id));
      toastUtil.success("댓글이 삭제되었습니다");
      onCommentDeleted?.();
    } catch (error) {
      console.error("댓글 삭제 실패:", error);
      toastUtil.error("댓글 삭제 중 오류가 발생했습니다");
    }
  }, [reviewId, comment.id, onCommentDeleted, isMine, replyCount]);

  const handleLongPressPointerDown = () => {
    if (!canDelete) return;

    clearLongPressTimer();
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      void requestDelete();
    }, COMMENT_LONG_PRESS_MS);
  };

  const handleLongPressPointerEnd = () => {
    clearLongPressTimer();
  };

  useEffect(() => {
    setIsLiked(comment.isLiked ?? false);
    setLikeCount(comment.likes);
  }, [comment.isLiked, comment.likes]);

  const handleLikeClick = useCallback(async () => {
    if (isToggling) return;

    const previousIsLiked = isLiked;
    const previousLikeCount = likeCount;
    const nextIsLiked = !previousIsLiked;
    const nextLikeCount = previousIsLiked
      ? previousLikeCount - 1
      : previousLikeCount + 1;
    // 낙관적 업데이트 (로컬 + 부모 동기화)
    setIsLiked(nextIsLiked);
    setLikeCount(nextLikeCount);
    onLikeChange?.(comment.id, nextIsLiked, nextLikeCount);
    setIsToggling(true);

    try {
      const response = await reviewApi.toggleCommentLike(
        parseInt(reviewId),
        parseInt(comment.id),
      );
      // 서버 확정값으로 로컬 + 부모 반영
      setIsLiked(response.is_liked);
      setLikeCount(response.like_count);
      onLikeChange?.(comment.id, response.is_liked, response.like_count);
    } catch (error) {
      // 실패 시 로컬 + 부모 모두 롤백
      setIsLiked(previousIsLiked);
      setLikeCount(previousLikeCount);
      onLikeChange?.(comment.id, previousIsLiked, previousLikeCount);
      console.error("댓글 좋아요 처리 실패:", error);
      toastUtil.error("댓글 좋아요 처리 중 오류가 발생했습니다");
    } finally {
      setIsToggling(false);
    }
  }, [reviewId, comment.id, isLiked, likeCount, isToggling, onLikeChange]);

  /** 언급된 아이디를 눌렀을 때 해당 프로필로 (아바타 탭과 같은 규칙) */
  const handleMentionClick = (userId: string) => {
    if (userId === currentUserId) {
      navigate("/main-my");
      return;
    }
    navigate(`/user/${userId}/profile`);
  };

  const handleCommentAvatarClick: React.MouseEventHandler<HTMLDivElement> = (
    e,
  ) => {
    e.stopPropagation();
    if (!comment.userId) {
      toastUtil.error("사용자 정보를 불러오는데 실패했습니다");
      return;
    }
    if (comment.userId === currentUserId) {
      navigate("/main-my");
      return;
    }
    navigate(`/user/${comment.userId}/profile`);
  };

  return (
    <div
      className={`
        w-full flex flex-col
        ${depth > 0 && depth <= MAX_DEPTH ? "pl-48px" : ""}
      `}
    >
      <div className="w-full flex">
        {/* 프로필 이미지 */}
        <Avatar
          nickname={authorDisplay}
          profileImage={comment.user.profileImage}
          size={36}
          onClick={handleCommentAvatarClick}
        />

        <Gap width={12} />

        <div className="flex-1 flex flex-col items-start">
          {/* 닉네임 및 내용 — 지울 수 있는 댓글은 1초 이상 길게 누르면 삭제 확인.
              삭제로 가는 길은 이것 하나다. '수정' 옆에 삭제 버튼을 두는 안은 접었다
              (2026-10-23) — 줄마다 글자가 하나 더 붙어 목록이 시끄러워진다. */}
          <div
            className={`
              w-full
              touch-pan-y
              ${canDelete ? "select-none" : ""}
            `}
            onPointerDown={canDelete ? handleLongPressPointerDown : undefined}
            onPointerUp={canDelete ? handleLongPressPointerEnd : undefined}
            onPointerLeave={canDelete ? handleLongPressPointerEnd : undefined}
            onPointerCancel={canDelete ? handleLongPressPointerEnd : undefined}
            onContextMenu={canDelete ? (e) => e.preventDefault() : undefined}
          >
            <p className="flex-1 font-pretendard font-regular text-14px leading-180% tracking-[-3%] text-textColorText2 break-all whitespace-pre-line">
              <strong className="font-bold">{authorDisplay}</strong>
              &nbsp;
              {/* 서버가 실제 언급으로 인정한 "@아이디"만 강조되고 프로필로 연결된다.
                  없는 아이디나 맞팔이 아닌 사람은 그냥 글자로 남는다. */}
              {splitMentions(comment.text, comment.mentions).map((segment, index) =>
                segment.type === "mention" ? (
                  <button
                    key={`${segment.userId}-${index}`}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMentionClick(segment.userId);
                    }}
                    className="bg-transparent border-none p-0 font-pretendard font-semibold text-14px leading-180% tracking-[-3%] text-brandPrimary cursor-pointer align-baseline"
                  >
                    {segment.text}
                  </button>
                ) : (
                  <React.Fragment key={index}>{segment.text}</React.Fragment>
                ),
              )}
            </p>
          </div>

          <Gap height={4} />

          <div className="flex items-center gap-8px">
            {/* 답글 달기 버튼 */}
            <button
              type="button"
              onClick={() => onReplyClick(comment.id)}
              className="font-pretendard font-regular text-14px leading-20px tracking-[-3%] text-textColorText3"
            >
              답글 달기
            </button>

            {/* 수정 버튼 - 내 댓글일 때만. 남의 말은 못 고친다. */}
            {isMine && onEditClick && (
              <button
                type="button"
                onClick={() => onEditClick(comment.id)}
                className="font-pretendard font-regular text-14px leading-20px tracking-[-3%] text-textColorText3"
              >
                수정
              </button>
            )}
          </div>
        </div>

        <Gap width={8} />

        {/* 좋아요 버튼 */}
        <button
          type="button"
          onClick={handleLikeClick}
          className="w-24px flex flex-col items-center gap-4px"
        >
          {/* 하트를 닉네임(첫 줄) 세로 중앙에 맞춤:
              닉네임 줄 line-box=14*1.8=25.2px → 중앙 12.6px, 하트14 중심=7 → +5.6px 내림 */}
          <div style={{ marginTop: 5.6 }}>
            <HeartIcon filled={isLiked} size={14} />
          </div>

          {likeCount > 0 && (
            <span className="font-pretendard font-regular text-12px leading-20px tracking-[-3%] text-brown-80%-main">
              {likeCount}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default ReviewCommentBox;
